/**
 * WineClubCTA — Card que invita al Consumidor Final a unirse al Club del
 * Vino Bacano y muestra el estado si ya está suscripto.
 *
 * Se renderiza en /account sólo cuando:
 *   - El club está `active` en settings.
 *   - El usuario es consumidor_final logueado.
 *
 * Estados que maneja:
 *   1) No suscripto → CTA con precio/perks y botón "Sumate ahora".
 *   2) Activa → confirmación con próxima fecha y botón "Cancelar".
 *   3) Cancelada → posibilidad de re-suscribirse.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Wine, Check, X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WineClubCTA = ({ user }) => {
  const [info, setInfo] = useState(null);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [i, s] = await Promise.all([
        axios.get(`${API}/wine-club/info`),
        axios.get(`${API}/wine-club/my-subscription`, { withCredentials: true }).catch(() => ({ data: { status: 'none' } })),
      ]);
      setInfo(i.data);
      setSub(s.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading || !info) return null;
  // Sólo consumidor final; y sólo si el club está activo.
  if (!info.active) return null;
  if (user?.customer_type && user.customer_type !== 'consumidor_final') return null;

  const money = (n) => `$${Math.round(n || 0).toLocaleString('es-AR')}`;
  const isActive = sub?.status === 'active';

  const subscribe = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/wine-club/subscribe`, {}, { withCredentials: true });
      toast.success('¡Bienvenido al Club del Vino Bacano! 🍷');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo suscribir');
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm('¿Cancelar tu suscripción al Club del Vino?')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/wine-club/cancel`, {}, { withCredentials: true });
      toast.success('Suscripción cancelada. Podés volver cuando quieras.');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cancelar');
    } finally {
      setBusy(false);
    }
  };

  // ─── UI: Suscripto activo ───
  if (isActive) {
    return (
      <div className="rounded-2xl border border-emerald-300 bg-gradient-to-br from-emerald-50 to-white p-4" data-testid="wine-club-card">
        <div className="flex items-start gap-3 mb-2">
          <div className="p-2 rounded-xl bg-emerald-600 text-white"><Wine size={18} /></div>
          <div className="flex-1">
            <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Suscripto</p>
            <h3 className="text-base font-black text-slate-900">{info.name}</h3>
            <p className="text-[11px] text-slate-500">
              Recibís <b>{info.bottles_per_month} vinos</b> por mes por {money(sub.price_snapshot)}. Alta el {sub.started_at?.slice(0, 10)}.
            </p>
          </div>
        </div>
        <button onClick={cancel} disabled={busy}
          className="w-full mt-2 py-2 rounded-lg text-xs font-medium text-rose-700 hover:bg-rose-50 border border-rose-200 disabled:opacity-40 flex items-center justify-center gap-1"
          data-testid="wine-club-cancel-btn">
          <X size={14} /> Cancelar suscripción
        </button>
      </div>
    );
  }

  // ─── UI: No suscripto (CTA) ───
  return (
    <div className="rounded-2xl border border-rose-200 bg-gradient-to-br from-rose-50 via-white to-amber-50 p-4" data-testid="wine-club-card">
      <div className="flex items-start gap-3 mb-3">
        <div className="p-2 rounded-xl bg-rose-600 text-white"><Wine size={18} /></div>
        <div className="flex-1">
          <div className="inline-flex items-center gap-1 mb-0.5">
            <Sparkles size={11} className="text-amber-500" />
            <p className="text-[10px] font-black text-amber-700 uppercase tracking-wider">Exclusivo Consumidor Final</p>
          </div>
          <h3 className="text-lg font-black text-slate-900 leading-tight">{info.name}</h3>
          <p className="text-xs text-slate-600 mt-1 leading-snug">{info.tagline}</p>
        </div>
      </div>

      <div className="mb-3 flex items-baseline gap-2">
        <span className="text-3xl font-black text-slate-900">{money(info.monthly_price)}</span>
        <span className="text-xs text-slate-500">/ mes · {info.bottles_per_month} botellas</span>
      </div>

      {info.perks?.length > 0 && (
        <ul className="space-y-1 mb-4">
          {info.perks.map((p, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
              <Check size={13} className="text-emerald-600 mt-0.5 flex-shrink-0" />
              <span>{p}</span>
            </li>
          ))}
        </ul>
      )}

      <button onClick={subscribe} disabled={busy}
        className="w-full py-3 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-black text-sm disabled:opacity-40 flex items-center justify-center gap-2"
        data-testid="wine-club-subscribe-btn">
        <Wine size={16} /> {busy ? 'Sumándote...' : 'Sumate al Club'}
      </button>
      <p className="text-[10px] text-slate-400 text-center mt-2">
        Podés pausar o cancelar cuando quieras. Sin permanencia.
      </p>
    </div>
  );
};

export default WineClubCTA;
